import React, { useEffect, useState } from 'react';
import './CatalogViewTab.css';
import useSuperAdminCatalogManagementStore from '@/stores/SuperAdminStore/CatalogManagementStore';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';

const CatalogViewTab: React.FC = () => {
  const { catalogCourses, categories, loading, error, fetchCatalogCourses, fetchCategories } = useSuperAdminCatalogManagementStore();

  const [filters, setFilters] = useState({
    categoryId: undefined as number | undefined,
    level: undefined as string | undefined,
    minDuration: undefined as number | undefined,
    maxDuration: undefined as number | undefined,
  });

  // Fetch categories and courses on mount
  useEffect(() => {
    fetchCategories();
    fetchCatalogCourses();
  }, [fetchCategories, fetchCatalogCourses]);

  // Fetch courses when filters change
  useEffect(() => {
    const activeFilters: any = {};
    if (filters.categoryId) activeFilters.categoryId = filters.categoryId;
    if (filters.level) activeFilters.level = filters.level;
    if (filters.minDuration) activeFilters.minDuration = filters.minDuration;
    if (filters.maxDuration) activeFilters.maxDuration = filters.maxDuration;

    fetchCatalogCourses(Object.keys(activeFilters).length > 0 ? activeFilters : undefined);
  }, [filters, fetchCatalogCourses]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, categoryId: value ? parseInt(value) : undefined }));
  };

  const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, level: value || undefined }));
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    let minDuration: number | undefined;
    let maxDuration: number | undefined;

    if (value === '1-4') {
      minDuration = 1;
      maxDuration = 4;
    } else if (value === '5-8') {
      minDuration = 5;
      maxDuration = 8;
    } else if (value === '9+') {
      minDuration = 9;
      maxDuration = undefined;
    }

    setFilters(prev => ({ ...prev, minDuration, maxDuration }));
  };

  return (
    <div className="admin-catalog-view-content">
      <div className="admin-catalog-filters">
        <select className="admin-catalog-filter-select" onChange={handleCategoryChange} value={filters.categoryId || ''}>
          <option value="">All Categories</option>
          {categories?.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select className="admin-catalog-filter-select" onChange={handleLevelChange} value={filters.level || ''}>
          <option value="">All Levels</option>
          <option value="Beginner">Beginner</option>
          <option value="Intermediate">Intermediate</option>
          <option value="Advanced">Advanced</option>
        </select>
        <select className="admin-catalog-filter-select" onChange={handleDurationChange} value={filters.minDuration ? `${filters.minDuration}-${filters.maxDuration || '+'}` : ''}>
          <option value="">All Durations</option>
          <option value="1-4">1-4 weeks</option>
          <option value="5-8">5-8 weeks</option>
          <option value="9+">9+ weeks</option>
        </select>
      </div>

      <div className="admin-catalog-preview-notice">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div>
          <strong>Preview Mode</strong>
          <p>This is how learners will see the catalog. Admin-only features are hidden in this view.</p>
        </div>
      </div>

      {loading && !catalogCourses && <LoadingSpinner message="Loading courses..." />}

      {error && (
        <ErrorDisplay
          message={error}
          type="error"
          onRetry={() => fetchCatalogCourses()}
        />
      )}

      {!loading && !error && catalogCourses && catalogCourses.length === 0 && (
        <div className="admin-catalog-empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>No Courses Found</h3>
          <p>There are no courses matching the selected filters.</p>
        </div>
      )}

      {!loading && !error && catalogCourses && catalogCourses.length > 0 && (
        <div className="admin-catalog-grid">
          {catalogCourses.map((course) => (
            <div key={course.id} className="admin-catalog-course-card">
              <div className="admin-catalog-course-card-image">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2V3zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7V3z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="admin-catalog-course-card-content">
                <h3 className="admin-catalog-course-card-title">{course.title}</h3>
                <p className="admin-catalog-course-card-category">{course.category}</p>
                <div className="admin-catalog-course-card-meta">
                  <div className="admin-catalog-course-rating">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#FFA500">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span>{course.rating}</span>
                  </div>
                  <span className="admin-catalog-course-duration">{course.duration}</span>
                </div>
                <span className={`admin-catalog-level-badge admin-catalog-${course.level.toLowerCase()}`}>
                  {course.level}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CatalogViewTab;
